import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { getArticles, getCategories } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import Newsletter from "@/components/Newsletter";

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["articles", activeCategory, debounced],
    queryFn: () =>
      getArticles({
        category: activeCategory,
        ...(debounced ? { search: debounced } : {}),
      }),
  });

  const setCategory = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") next.delete("category");
    else next.set("category", slug);
    setSearchParams(next);
  };

  const tabs = [{ slug: "all", name: "All" }, ...categories];

  return (
    <div data-testid="blog-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-6">
        <span className="font-sans text-sm uppercase tracking-wider text-[#D96B43]">
          The Journal
        </span>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-[#2C3529] mt-3 max-w-2xl leading-[1.05]">
          Stories & guides for a well-loved pet
        </h1>
        <p className="font-sans text-lg text-[#576054] mt-5 max-w-xl leading-relaxed">
          Browse our growing collection of gentle, practical writing on caring
          for the animals who mean the world to you.
        </p>
      </section>

      {/* Controls */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.slug}
              onClick={() => setCategory(t.slug)}
              data-testid={`filter-${t.slug}`}
              className={`rounded-full px-5 py-2 font-sans text-sm font-medium transition-all duration-300 ${
                activeCategory === t.slug
                  ? "bg-[#2C3529] text-white"
                  : "bg-[#FAF9F6] text-[#576054] border border-[#EAE4D9] hover:border-[#D96B43] hover:text-[#D96B43]"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a9184]" strokeWidth={1.5} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles..."
            data-testid="search-input"
            className="w-full rounded-full bg-[#FAF9F6] border border-[#EAE4D9] pl-11 pr-10 py-2.5 font-sans text-sm text-[#2C3529] outline-none focus:ring-2 focus:ring-[#D96B43]/40 focus:border-[#D96B43] transition"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              data-testid="clear-search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a9184] hover:text-[#D96B43]"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 min-h-[40vh]">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-[#EAE4D9] rounded-[2rem] aspect-[4/3] mb-6" />
                <div className="h-4 bg-[#EAE4D9] rounded w-1/3 mb-3" />
                <div className="h-6 bg-[#EAE4D9] rounded w-4/5" />
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div data-testid="no-results" className="text-center py-20">
            <p className="font-serif text-3xl text-[#2C3529]">No articles found</p>
            <p className="font-sans text-[#576054] mt-2">
              Try a different category or search term.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-14">
            {articles.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} />
            ))}
          </div>
        )}
      </section>

      <Newsletter />
    </div>
  );
}
